import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Settings as SettingsIcon, Save, CalendarDays, Copy, RefreshCcw, PenLine, Upload, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [gcal, setGcal] = useState(null);
  const [gcalLoading, setGcalLoading] = useState(false);
  const [diagnose, setDiagnose] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then(r => setForm(r.data));
    loadGcal();
  }, []);

  const loadGcal = () => {
    setGcalLoading(true);
    Promise.all([
      api.get("/admin/gcal/calendars"),
      api.get("/admin/gcal/diagnose").catch(() => ({ data: null })),
    ]).then(([cal, diag]) => {
      setGcal(cal.data);
      setDiagnose(diag.data);
    }).finally(() => setGcalLoading(false));
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Impostazioni salvate");
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiato"); };

  if (!form) return <div className="text-[#6B6558]">Caricamento…</div>;

  return (
    <div data-testid="section-settings">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-2"><SettingsIcon className="w-3 h-3" /> Configurazione</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">Impostazioni Associazione</h1>
        <p className="text-[#6B6558] mt-2">Questi dati appaiono sulle ricevute PDF, sulle tessere e nelle email automatiche.</p>
      </div>

      <form onSubmit={save} className="paper rounded-2xl p-6 space-y-5 max-w-2xl mb-6">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Presidente</label>
          <input data-testid="president-input" value={form.president_name || ""} onChange={e => update("president_name", e.target.value)}
            className="mt-1 w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          <p className="text-xs text-[#8A8272] mt-1">Firma che appare in fondo alla ricevuta fiscale.</p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Nome Associazione</label>
          <input data-testid="assoc-name-input" value={form.association_name || ""} onChange={e => update("association_name", e.target.value)}
            className="mt-1 w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Sottotitolo (natura giuridica)</label>
          <input data-testid="assoc-subtitle-input" value={form.association_subtitle || ""} onChange={e => update("association_subtitle", e.target.value)}
            className="mt-1 w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Indirizzo / Sede</label>
          <input data-testid="assoc-address-input" value={form.association_address || ""} onChange={e => update("association_address", e.target.value)}
            className="mt-1 w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Codice Fiscale / P.IVA</label>
          <input data-testid="assoc-tax-input" value={form.association_tax_id || ""} onChange={e => update("association_tax_id", e.target.value)}
            className="mt-1 w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Link PayPal.me</label>
          <input data-testid="paypal-input" value={form.paypal_me || ""} onChange={e => update("paypal_me", e.target.value)}
            className="mt-1 w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Numero iniziale ricevute</label>
          <input data-testid="receipt-start-input" type="number" min="1" value={form.receipt_start_seq ?? 1}
            onChange={e => update("receipt_start_seq", parseInt(e.target.value) || 1)}
            className="mt-1 w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          <p className="text-xs text-[#8A8272] mt-1">La prossima ricevuta partirà da questo numero (es. 20 se ne hai già emesse 19 su carta). Formato: NNN/anno.</p>
        </div>

        <div className="border-t border-[#E8E2D8] pt-5">
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold flex items-center gap-1"><PenLine className="w-3 h-3" /> Firma Digitale del Presidente</label>
          <p className="text-xs text-[#8A8272] mt-1 mb-2">
            Carica un'immagine PNG con sfondo trasparente della firma. Verrà applicata automaticamente su tutti i PDF verbali insieme all'hash SHA-256 di integrità e a un QR di verifica pubblica.
          </p>
          {form.president_signature_file_id ? (
            <div className="flex items-center gap-3 bg-white border border-[#E8E2D8] rounded-lg p-3">
              <img
                src={`${API}/files/${form.president_signature_file_id}/download`}
                alt="Firma Presidente"
                className="h-16 object-contain bg-[#FAF7F2] rounded px-2"
                data-testid="president-signature-preview"
              />
              <div className="flex-1 text-xs">
                <div className="text-[#2B3A33] font-semibold">Firma caricata</div>
                <div className="text-[#8A8272] font-mono truncate">{form.president_signature_file_id}</div>
              </div>
              <button
                type="button"
                data-testid="remove-signature-btn"
                onClick={() => update("president_signature_file_id", "")}
                className="text-[#EF3E36] p-2 rounded hover:bg-[#FDF2F2]"
                title="Rimuovi firma"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="mt-1 flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-[#E8E2D8] bg-white cursor-pointer hover:bg-[#FAF7F2]">
              <Upload className="w-4 h-4 text-[#EF3E36]" />
              <span className="text-sm text-[#6B6558]">Carica firma (PNG/JPG, max 2 MB)</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                data-testid="signature-file-input"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 2 * 1024 * 1024) { toast.error("File troppo grande (max 2 MB)"); return; }
                  const fd = new FormData();
                  fd.append("file", f);
                  fd.append("kind", "signature");
                  try {
                    const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    update("president_signature_file_id", up.data.id);
                    toast.success("Firma caricata — ora clicca Salva");
                  } catch { toast.error("Errore upload firma"); }
                }}
              />
            </label>
          )}
        </div>

        <div className="border-t border-[#E8E2D8] pt-5">
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Link Gruppo WhatsApp Community</label>
          <input data-testid="wa-community-input" value={form.community_whatsapp_url || ""} onChange={e => update("community_whatsapp_url", e.target.value)}
            placeholder="https://chat.whatsapp.com/XXXXXXXXX"
            className="mt-1 w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8] font-mono text-xs" />
          <p className="text-xs text-[#8A8272] mt-1">
            Incolla qui l'invito al gruppo Community (WhatsApp → info gruppo → Invita tramite link).
            Serve per il pulsante <em>"Vai al Gruppo"</em> nella sezione Community.
          </p>
        </div>

        <div className="border-t border-[#E8E2D8] pt-5">
          <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Google Calendar ID</label>
          <input data-testid="gcal-input" value={form.google_calendar_id || ""} onChange={e => update("google_calendar_id", e.target.value)}
            placeholder="c_XXXXXX@group.calendar.google.com"
            className="mt-1 w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8] font-mono text-xs" />
          <p className="text-xs text-[#8A8272] mt-1">Quando impostato, ogni riunione creata viene sincronizzata automaticamente su Google Calendar.</p>
        </div>

        <button type="submit" data-testid="save-settings-btn" disabled={saving}
          className="terracotta-btn px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Salvo…" : "Salva impostazioni"}
        </button>
      </form>

      {/* Google Calendar diagnostics */}
      {gcal && (
        <div className="paper rounded-2xl p-6 max-w-2xl" style={{background: "linear-gradient(135deg, #FFFFFF, #FAF7F2)"}}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Google Calendar</div>
              <div className="font-display text-lg font-bold text-[#2B3A33] mt-1">Calendari accessibili</div>
            </div>
            <button onClick={loadGcal} disabled={gcalLoading} className="text-xs px-3 py-1.5 rounded-lg border border-[#E8E2D8] bg-white hover:bg-[#F3EEE4] inline-flex items-center gap-1">
              <RefreshCcw className="w-3 h-3" /> {gcalLoading ? "Ricarico…" : "Aggiorna"}
            </button>
          </div>

          {gcal.service_account_email && (
            <div className="mb-4 p-3 rounded-lg bg-white border border-[#E8E2D8]">
              <div className="text-xs text-[#6B6558] mb-1">Email del Service Account (condividi il tuo calendario con questa email):</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono break-all">{gcal.service_account_email}</code>
                <button onClick={() => copy(gcal.service_account_email)} className="text-xs px-2 py-1 rounded border border-[#E8E2D8] hover:bg-[#FDF2F2] inline-flex items-center gap-1"><Copy className="w-3 h-3" /></button>
              </div>
            </div>
          )}

          {diagnose && (
            <div
              data-testid="gcal-diagnose-banner"
              className={`mb-4 p-4 rounded-lg text-sm ${
                diagnose.can_write
                  ? "bg-[#D1FAE5] border border-[#6EE7B7] text-[#065F46]"
                  : "bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B]"
              }`}
            >
              <div className="font-bold mb-1">
                {diagnose.can_write ? "✅ Google Calendar collegato e funzionante" : "⚠️ Google Calendar non funziona"}
              </div>
              <div className="text-xs">
                {diagnose.target_summary && diagnose.can_write && (
                  <>Calendario: <strong>{diagnose.target_summary}</strong> · le riunioni create nell'app vengono sincronizzate automaticamente.</>
                )}
                {!diagnose.can_write && diagnose.next_step}
              </div>
              {!diagnose.can_write && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button
                    data-testid="gcal-retry-btn"
                    onClick={loadGcal}
                    className="text-xs px-3 py-1.5 rounded border border-[#991B1B] text-[#991B1B] font-semibold hover:bg-white/40">
                    🔄 Riprova
                  </button>
                </div>
              )}
              {diagnose.can_write && (
                <button
                  data-testid="gcal-backfill-btn"
                  onClick={async () => {
                    if (!window.confirm("Provo a sincronizzare TUTTE le riunioni non ancora su Google Calendar. Continuare?")) return;
                    try {
                      const r = await api.post("/admin/gcal/backfill");
                      toast.success(`Sincronizzate ${r.data.synced} riunioni · ${r.data.failed} fallite`);
                      loadGcal();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Errore backfill");
                    }
                  }}
                  className="mt-2 text-xs px-3 py-1.5 rounded border border-[#065F46] text-[#065F46] font-semibold hover:bg-white/40">
                  🔄 Sincronizza riunioni non ancora su Google
                </button>
              )}
            </div>
          )}

          {!gcal.configured && (
            <div className="text-sm text-[#EF3E36] bg-[#FDF2F2] rounded-lg p-3">Service account non caricato: verifica il file JSON.</div>
          )}

          {gcal.configured && gcal.calendars.length === 0 && !diagnose?.can_write && (
            <div className="text-sm text-[#6B6558] bg-[#FEF3C7] rounded-lg p-4 space-y-2">
              <div className="font-semibold text-[#92400E]">⚠️ Nessun calendario condiviso ancora</div>
              <div>Per collegare un calendario:</div>
              <ol className="list-decimal ml-5 space-y-1 text-xs">
                <li>Apri <a className="underline text-[#EF3E36]" href="https://calendar.google.com" target="_blank" rel="noreferrer">Google Calendar</a></li>
                <li>Trova il calendario "Associazione Amunì" (o creane uno) → 3 puntini → <b>Impostazioni e condivisione</b></li>
                <li>In "Condividi con persone specifiche" → <b>Aggiungi</b> l'email sopra ↑</li>
                <li>Permesso: <b>"Apportare modifiche agli eventi"</b> → Invia</li>
                <li>Torna qui e clicca <b>Aggiorna</b> — vedrai il calendario apparire nella lista</li>
              </ol>
              <div className="pt-2 border-t border-[#FCD34D] mt-2">
                <button
                  data-testid="gcal-diagnose-btn"
                  onClick={async () => {
                    try {
                      const r = await api.get("/admin/gcal/diagnose");
                      const d = r.data;
                      const lines = [
                        `📄 File JSON: ${d.service_account_file_present ? "✅" : "❌"}`,
                        `📧 Service Account: ${d.service_account_email || "—"}`,
                        `📅 Calendar ID: ${d.target_calendar_id || "(non impostato)"}`,
                        `👀 Raggiungibile: ${d.target_reachable ? "✅" : "❌"}`,
                        `✏️ Può scrivere: ${d.can_write ? "✅" : "❌"}`,
                        "",
                        `➡️ ${d.next_step || "—"}`,
                      ];
                      if (d.error) lines.push("", `Errore: ${d.error}`);
                      alert(lines.join("\n"));
                    } catch (err) {
                      toast.error("Errore diagnosi: " + (err?.response?.data?.detail || err.message));
                    }
                  }}
                  className="text-xs px-3 py-1.5 rounded border border-[#92400E] text-[#92400E] font-semibold hover:bg-[#FEF3C7]">
                  🔍 Esegui diagnosi
                </button>
                <button
                  data-testid="gcal-backfill-btn"
                  onClick={async () => {
                    if (!window.confirm("Provo a sincronizzare TUTTE le riunioni non ancora su Google Calendar. Continuare?")) return;
                    try {
                      const r = await api.post("/admin/gcal/backfill");
                      toast.success(`Sincronizzate ${r.data.synced} riunioni · ${r.data.failed} fallite`);
                      loadGcal();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Errore backfill");
                    }
                  }}
                  className="ml-2 text-xs px-3 py-1.5 rounded border border-[#E8E2D8] text-[#2B3A33] font-semibold hover:bg-white">
                  🔄 Sincronizza riunioni esistenti
                </button>
              </div>
            </div>
          )}

          {gcal.configured && gcal.calendars.length > 0 && (
            <div className="space-y-2">
              {gcal.calendars.map(c => (
                <div key={c.id} className="flex items-center gap-2 bg-white border border-[#E8E2D8] rounded-lg p-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#2B3A33]">{c.summary}</div>
                    <div className="text-xs text-[#6B6558] font-mono truncate">{c.id}</div>
                    <div className="text-[10px] uppercase tracking-wide text-[#8A8272] mt-1">Accesso: {c.access_role}</div>
                  </div>
                  <button
                    onClick={() => { update("google_calendar_id", c.id); toast.info("ID impostato — clicca Salva impostazioni"); }}
                    className="text-xs px-3 py-1.5 rounded-lg terracotta-btn font-semibold">
                    Usa questo
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
